// src/modules/purityAnalyzer/pages/CalibrationVersions.jsx
// Calibration dataset versions: lineage, quality stats, point tables,
// activate / rollback. Versions are append-only.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Tag, Typography, Space, Button, message, Drawer, Descriptions,
  Popconfirm, Statistic, Row, Col, Modal, Form, InputNumber, Input, Checkbox,
} from 'antd';
import AdvancedTable from '../../../components/UI/AdvancedTable';
import {
  DeploymentUnitOutlined, CheckCircleOutlined, RollbackOutlined, EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchVersions, fetchVersion, activateVersion, addManualPoint } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';
import { VERSION_STATUS_COLORS, QUALITY_COLORS } from '../constants/purityConstants';

const { Title, Text } = Typography;

const POINT_SOURCE_COLORS = { SEED: 'default', MANUAL: 'blue', XRF_LEARNED: 'green' };

export default function CalibrationVersions() {
  const { can } = usePermissions();
  const canActivate = can('purityAnalyzer', 'activateCalibration');
  const canAddPoint = can('purityAnalyzer', 'approveCalibration');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [pointTarget, setPointTarget] = useState(null);
  const [savingPoint, setSavingPoint] = useState(false);
  const [pointForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchVersions();
      setRows(res?.data?.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await fetchVersion(id);
      setDetail(res?.data?.data || null);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load version');
    }
  };

  const onActivate = async (id, rollback) => {
    try {
      await activateVersion(id, rollback);
      message.success(rollback ? 'Rolled back to this version' : 'Version activated');
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Activation failed');
    }
  };

  const onAddPoint = async () => {
    try {
      const values = await pointForm.validateFields();
      setSavingPoint(true);
      const res = await addManualPoint(pointTarget._id, values);
      const version = res?.data?.data?.version;
      message.success(
        `New calibration version ${version?.versionNumber || ''} created with the manual point${values.activate ? ' (ACTIVATED)' : ''}`
      );
      setPointTarget(null);
      pointForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to add point');
    } finally {
      setSavingPoint(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        <DeploymentUnitOutlined style={{ marginRight: 10 }} />Calibration Dataset Versions
      </Title>

      <AdvancedTable
        rowKey="_id" size="small" loading={loading} dataSource={rows}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: 'Version', dataIndex: 'versionNumber', width: 110, key: 'versionNumber' },
          { title: 'Label', dataIndex: 'versionLabel', width: 150, key: 'versionLabel' },
          {
            title: 'Status', dataIndex: 'status', width: 100, key: 'status',
            render: (v) => <Tag color={VERSION_STATUS_COLORS[v]}>{v}</Tag>,
          },
          { title: 'Metal', dataIndex: 'metalType', width: 90, key: 'metalType' },
          {
            title: 'Machine', width: 120, key: 'machine',
            render: (_, r) => r.machine ? `${r.machine.machineCode}` : <Tag>Global</Tag>,
          },
          {
            title: 'Points', dataIndex: ['qualityStats', 'pointCount'], width: 80, key: 'pointCount',
          },
          {
            title: 'RMSE', width: 90, key: 'rmse',
            render: (_, r) => r.qualityStats?.rmse != null ? `${r.qualityStats.rmse}%` : '—',
          },
          {
            title: 'Based On', width: 120, key: 'basedOnVersion',
            render: (_, r) => r.basedOnVersion?.versionNumber || '—',
          },
          {
            title: 'Created', dataIndex: 'createdAt', width: 130, key: 'createdAt',
            render: (v) => dayjs(v).format('DD MMM YY · HH:mm'),
          },
          {
            title: '', width: 210, fixed: 'right', key: 'actions',
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r._id)}>
                  Points
                </Button>
                {canActivate && r.status === 'DRAFT' && (
                  <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                    onClick={() => onActivate(r._id, false)}>
                    Activate
                  </Button>
                )}
                {canAddPoint && r.status !== 'ARCHIVED' && (
                  <Button size="small" icon={<PlusOutlined />} onClick={() => setPointTarget(r)}>
                    Add Point
                  </Button>
                )}
                {canActivate && r.status === 'ARCHIVED' && (
                  <Popconfirm title="Roll back to this dataset version?"
                    onConfirm={() => onActivate(r._id, true)}>
                    <Button size="small" icon={<RollbackOutlined />}>Rollback</Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title={`${detail?.versionNumber || ''} — ${detail?.versionLabel || ''}`}
        open={!!detail} onClose={() => setDetail(null)} width={700}
      >
        {detail && (
          <>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={6}><Statistic title="Points" value={detail.qualityStats?.pointCount ?? detail.points?.length} /></Col>
              <Col span={6}><Statistic title="Verified Tests" value={detail.qualityStats?.verifiedTestCount ?? 0} /></Col>
              <Col span={6}><Statistic title="RMSE %" value={detail.qualityStats?.rmse ?? '—'} /></Col>
              <Col span={6}><Statistic title="Avg Error %" value={detail.qualityStats?.avgAbsError ?? '—'} /></Col>
            </Row>
            <Descriptions column={2} size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Status">
                <Tag color={VERSION_STATUS_COLORS[detail.status]}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Based On">{detail.basedOnVersion?.versionNumber || '—'}</Descriptions.Item>
              <Descriptions.Item label="Created By">{detail.createdBy?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Activated By">{detail.activatedBy?.name || '—'}</Descriptions.Item>
            </Descriptions>
            <AdvancedTable
              rowKey="_id" size="small" pagination={false}
              dataSource={detail.points || []}
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: 'Density (g/cm³)', dataIndex: 'density', width: 140, key: 'density',
                  render: (v) => <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</Text>,
                },
                { title: 'Purity (%)', dataIndex: 'purity', width: 100, key: 'purity' },
                { title: 'Machine Rdg', dataIndex: 'machineReading', width: 110, key: 'machineReading', render: (v) => v ?? '—' },
                { title: 'XRF Rdg', dataIndex: 'xrfReading', width: 90, key: 'xrfReading', render: (v) => v ?? '—' },
                {
                  title: 'Source', dataIndex: 'source', width: 150, key: 'source',
                  render: (v, r) => (
                    <Space size={4}>
                      <Tag color={POINT_SOURCE_COLORS[v]}>{v}</Tag>
                      {r.sourceTest?.testNumber && <Tag>{r.sourceTest.testNumber}</Tag>}
                    </Space>
                  ),
                },
                {
                  title: 'Status', dataIndex: 'status', width: 90, key: 'status',
                  render: (v) => <Tag color={v === 'ACTIVE' ? 'green' : 'orange'}>{v}</Tag>,
                },
              ]}
            />
          </>
        )}
      </Drawer>

      <Modal
        title={`Add Manual Point — based on ${pointTarget?.versionNumber || ''}`}
        open={!!pointTarget}
        onCancel={() => { setPointTarget(null); pointForm.resetFields(); }}
        onOk={onAddPoint}
        okText="Add Point & Create Version"
        confirmLoading={savingPoint}
      >
        <Text type="secondary">
          Versions are append-only: this clones {pointTarget?.versionNumber} into a new
          calibration version containing the extra point. Previous versions remain
          available for rollback.
        </Text>
        <Form form={pointForm} layout="vertical" style={{ marginTop: 12 }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="density" label="Density (g/cm³)"
                rules={[{ required: true, message: 'Density is required' }]}
              >
                <InputNumber min={0.001} step={0.001} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="purity" label="Purity (%)"
                rules={[{ required: true, message: 'Purity is required' }]}
              >
                <InputNumber min={0.01} max={100} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="label" label="Version Label">
            <Input placeholder="e.g. Reference sample 22K bar" />
          </Form.Item>
          <Form.Item name="activate" valuePropName="checked">
            <Checkbox>Activate the new version immediately</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
